import { useRef } from 'react';

import isEqual from 'lodash-es/isEqual';

import { Extension } from '@atlassian/forge-ui';

import { ForgeUIExtensionDataType, ForgeUIType } from '../types';

const capitalize = (str: string): string => {
  if (!str || !str.length) {
    return '';
  }
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const extensionTitle = (
  extension: Extension,
  overriddenTitle?: string | null
): string => {
  const title = overriddenTitle || extension.properties.title;
  let env = '';
  if (extension.environmentType !== 'PRODUCTION') {
    env = ` (${capitalize(extension.environmentType || '')})`;
  }
  return title + env;
};

export const getAppId = (app: Extension): string | undefined => {
  if (!app.id) {
    return undefined;
  }

  const [, id] = app.id.split('/');
  return id;
};

export const getForgeUIType = (
  extension: Extension,
  entryPoint = ''
): ForgeUIType => {
  const { render, resource } = entryPoint
    ? extension.properties[entryPoint]
    : extension.properties;

  if (resource) {
    if (render === 'native') {
      return ForgeUIType.UI_KIT_2;
    }

    return ForgeUIType.CUSTOM_UI;
  }

  return ForgeUIType.UI_KIT_1;
};

export const filterExtensions = (
  extensions: readonly Extension[],
  customFilterPredicate: (extension: Extension) => boolean = () => true,
  includeHostedResources: boolean,
  hostedResourcesEnabledExtensionPoints: string[]
): Extension[] => {
  return extensions.filter(ext => {
    if (!customFilterPredicate(ext)) {
      return false;
    }
    if (getForgeUIType(ext) === ForgeUIType.CUSTOM_UI) {
      return (
        includeHostedResources &&
        hostedResourcesEnabledExtensionPoints.includes(ext.type)
      );
    } else {
      return true;
    }
  });
};

export const getLocalId = (
  extensionId: string,
  extensionType: string
): string => {
  return `${extensionId}-${extensionType}`.replace(/[^a-zA-Z0-9-]/g, '-');
};

export function useMemoedExtensionData(
  extensionData: ForgeUIExtensionDataType
): ForgeUIExtensionDataType {
  const ref = useRef(extensionData);
  if (!isEqual(ref.current, extensionData)) {
    ref.current = extensionData;
  }
  return ref.current;
}
